import { HighlightStyle, StreamLanguage, StringStream, syntaxHighlighting } from '@codemirror/language';
import { Completion, CompletionContext, CompletionResult, snippetCompletion } from '@codemirror/autocomplete';
import { Diagnostic } from '@codemirror/lint';
import { Extension } from '@codemirror/state';
import { Text } from '@codemirror/state';
import { tags } from '@lezer/highlight';
import { DiagnosticOutDTO, DiagnosticSeverityEnum, FactTypeEnum, RuleMetadataOutDTO } from '@api/ringil-api.v2';
import { enumValueLabel, factGroupLabel, paramEnumKeyPrefix } from '../../i18nKeys';
import { metadataRefOptions } from '../builder/valueDescriptor';
import { argValueLabel, factParametersOf } from '../../factArguments';

/**
 * Syntax support for the rule DSL.
 *
 * The grammar is fixed (`dsl/Tokens.kt`), so keywords and units are declared here; the
 * *vocabulary* — triggers, facts, operators, actions, enum values, reserved words — is never
 * hard-coded and always comes from the metadata endpoint.
 */

/** `Keywords.STRUCTURAL`. */
const KEYWORDS = new Set([
  'rule',
  'enabled',
  'disabled',
  'priority',
  'on',
  'after',
  'when',
  'then',
  'and',
  'or',
  'not',
  'in',
  'contains',
  'starts',
  'with',
  'is',
  'null',
  'between',
  'true',
  'false',
]);

/**
 * `group` is a keyword only right after `in` / `not in` (`in group EU`); anywhere else it is an
 * ordinary word, so it is recognised by what precedes it on the line rather than listed above.
 */
const GROUP_KEYWORD = 'group';
const PRECEDED_BY_IN = /\bin\s+$/;

/**
 * `now` likewise: a keyword only where a value goes — after a comparison operator, or as either
 * bound of `between … and …`. Elsewhere it is an ordinary word.
 */
const NOW_KEYWORD = 'now';
const PRECEDED_BY_VALUE_POSITION = /(==|!=|>=|<=|>|<|\bbetween|\band)\s*$/;

/** `Keywords.QUANTITY_UNITS` and `Keywords.DURATION_UNITS`. */
const UNITS = new Set(['kg', 't', 'km', 'minute', 'minutes', 'hour', 'hours', 'day', 'days', 'week', 'weeks']);

interface LanguageState {
  inBlockComment: boolean;
}

const tokenTable = {
  ruleKeyword: tags.keyword,
  ruleComment: tags.comment,
  ruleString: tags.string,
  ruleNumber: tags.number,
  ruleFact: tags.variableName,
  ruleAction: tags.function(tags.variableName),
  ruleOperator: tags.operator,
  ruleUnit: tags.unit,
  ruleAtom: tags.atom,
  ruleReserved: tags.invalid,
};

const consumeBlockComment = (stream: StringStream, state: LanguageState): string => {
  while (!stream.eol()) {
    if (stream.match('*/')) {
      state.inBlockComment = false;
      return 'ruleComment';
    }
    stream.next();
  }
  return 'ruleComment';
};

/**
 * Reserved words are highlighted as errors rather than identifiers: the grammar sets them aside
 * for chaining and waits that do not exist yet, and a rule that used one as a fact name could
 * never be made to parse later.
 */
export const createRuleLanguage = (reservedWords: string[]): StreamLanguage<LanguageState> => {
  const reserved = new Set(reservedWords);

  return StreamLanguage.define<LanguageState>({
    name: 'ringil-rule',
    startState: () => ({ inBlockComment: false }),
    token(stream, state) {
      if (state.inBlockComment) return consumeBlockComment(stream, state);
      if (stream.eatSpace()) return null;

      if (stream.match('//') || stream.match('#')) {
        stream.skipToEnd();
        return 'ruleComment';
      }
      if (stream.match('/*')) {
        state.inBlockComment = true;
        return consumeBlockComment(stream, state);
      }

      if (stream.peek() === '"') {
        stream.next();
        let escaped = false;
        while (!stream.eol()) {
          const char = stream.next();
          if (escaped) {
            escaped = false;
          } else if (char === '\\') {
            escaped = true;
          } else if (char === '"') {
            break;
          }
        }
        return 'ruleString';
      }

      // A `-` directly before a digit is a negative number (`-5`, `now -2 hours`); otherwise `+` and
      // `-` are the offset in `now + 2 hours`.
      if (stream.match(/^-?\d+(\.\d+)?/)) return 'ruleNumber';
      if (stream.eat('%')) return 'ruleUnit';
      if (stream.match(/^(==|!=|>=|<=|>|<|\+|-)/)) return 'ruleOperator';

      const identifier = stream.match(/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)*/) as RegExpMatchArray | null;
      if (identifier) {
        const word = identifier[0];
        if (reserved.has(word)) return 'ruleReserved';
        if (KEYWORDS.has(word)) return 'ruleKeyword';
        if (word === GROUP_KEYWORD && PRECEDED_BY_IN.test(stream.string.slice(0, stream.start))) return 'ruleKeyword';
        if (word === NOW_KEYWORD && PRECEDED_BY_VALUE_POSITION.test(stream.string.slice(0, stream.start))) return 'ruleKeyword';
        if (UNITS.has(word)) return 'ruleUnit';
        // Facts are dotted and actions are not, so a fact taking arguments —
        // `expedition.shipmentDuration(first: min)` — stays a fact despite the '('.
        if (word.includes('.')) return 'ruleFact';
        // An undotted identifier immediately followed by '(' is an action call.
        if (stream.peek() === '(') return 'ruleAction';
        return 'ruleAtom';
      }

      stream.next();
      return null;
    },
    tokenTable,
  });
};

/** Colours drawn from the app's own tokens rather than a stock editor theme. */
export const ruleHighlighting: Extension = syntaxHighlighting(
  HighlightStyle.define([
    { tag: tags.keyword, color: '#FE5000', fontWeight: '500' },
    { tag: tags.comment, color: '#8C8C8C', fontStyle: 'italic' },
    { tag: tags.string, color: '#0A7C42' },
    { tag: tags.number, color: '#0A6CBD' },
    { tag: tags.unit, color: '#0A6CBD' },
    { tag: tags.variableName, color: '#1F2933' },
    { tag: tags.function(tags.variableName), color: '#6B2FB5', fontWeight: '500' },
    { tag: tags.operator, color: '#8C4B00' },
    { tag: tags.atom, color: '#0A6CBD' },
    { tag: tags.invalid, color: '#C0392B', textDecoration: 'underline wavy' },
  ]),
);

// ---------------------------------------------------------------------------
// Completion
// ---------------------------------------------------------------------------

type Section = 'HEADER' | 'TRIGGER' | 'CONDITION' | 'ACTION';

/** Which clause the cursor sits in, from the last section keyword before it. */
const sectionAt = (before: string): Section => {
  const lastIndexOfWord = (word: string) => {
    const match = before.match(new RegExp(`(^|[^A-Za-z0-9_.])${word}([^A-Za-z0-9_.]|$)`, 'g'));
    return match ? before.lastIndexOf(match[match.length - 1]) : -1;
  };
  const on = lastIndexOfWord('on');
  const when = lastIndexOfWord('when');
  const then = lastIndexOfWord('then');
  const furthest = Math.max(on, when, then);
  if (furthest < 0) return 'HEADER';
  if (furthest === then) return 'ACTION';
  if (furthest === when) return 'CONDITION';
  return 'TRIGGER';
};

const option = (label: string, detail: string, type: string): Completion => ({ label, detail, type });

/**
 * A fact as written in a condition, with its arguments if any — `expedition.distance`,
 * `expedition.shipmentDuration(first: max, last: min)`. Group 1 is the fact's key.
 */
const FACT_REF = String.raw`([A-Za-z_][A-Za-z0-9_.]*)(?:\s*\([^()]*\))?`;

/**
 * Duration values to start from, as snippets so the author tabs through amount and unit. The DSL
 * keywords are inserted untranslated, like every other part of the rule text.
 */
const DURATION_EXAMPLES: Array<[number, string]> = [
  [48, 'hours'],
  [2, 'days'],
  [90, 'minutes'],
  [1, 'week'],
];

/** `useTranslationLgs`' `tr`, passed in because this module is not a component. */
type Translate = (key: string, defaultText?: string, data?: unknown) => string;

/**
 * Fetches the values of an `entityRefType` (countries), or undefined when the text editor offers
 * none for it. Passed in, like `tr`, because the lists come from the API and its cache.
 */
export type RefOptionsLoader = (entityRefType: string) => Promise<RefItem[]> | undefined;

interface RefItem {
  value: string;
  label: string;
}

/**
 * CodeMirror filters on the inserted text, so typing `Germ` would never find `DEU`. Fetched lists
 * are filtered here instead: codes starting with what was typed first, then names containing it.
 */
export const matchRefItems = (items: RefItem[], typed: string): RefItem[] => {
  const query = typed.toLowerCase();
  if (!query) return items;
  const codeMatches = items.filter(item => item.value.toLowerCase().startsWith(query));
  const nameMatches = items.filter(item => !item.value.toLowerCase().startsWith(query) && item.label.toLowerCase().includes(query));
  return [...codeMatches, ...nameMatches];
};

/**
 * Completions come from the metadata endpoint, narrowed by where the cursor is: triggers after
 * `on`, facts after `when`, the operators legal for the fact just typed, that fact's values after
 * an operator or inside `in [...]` (enum values, or a fetched list such as countries), actions
 * after `then`, and an action's parameter names inside its call.
 *
 * The label is always the DSL spelling — that is what gets inserted — and the detail beside it is
 * what the catalogue says about it, translated. The `i18nKey`s are only keys; printing them raw,
 * as this used to, put `aidispatcher.trigger.expedition_created` in the popup.
 */
export const createRuleCompletion = (metadata: RuleMetadataOutDTO | undefined, tr: Translate, loadRefOptions?: RefOptionsLoader) => {
  return (context: CompletionContext): CompletionResult | null | Promise<CompletionResult | null> => {
    if (!metadata) return null;

    const word = context.matchBefore(/[A-Za-z0-9_."%]*/);
    if (!word) return null;
    if (word.from === word.to && !context.explicit) return null;

    const before = context.state.sliceDoc(0, word.from);
    const section = sectionAt(before);

    if (section === 'TRIGGER') {
      return { from: word.from, options: metadata.triggers.map(trigger => option(trigger.key, tr(trigger.i18nKey, trigger.key), 'event')) };
    }

    if (section === 'ACTION') {
      // Only the actions are scanned: a bracket left open in the condition is not this call's.
      const lastThen = before.match(/(^|[^A-Za-z0-9_.])then(?=[^A-Za-z0-9_.]|$)/g)?.pop();
      const frames = openBrackets(lastThen ? before.slice(before.lastIndexOf(lastThen) + lastThen.length) : before);
      const top = frames[frames.length - 1];
      const call = frames[0]?.bracket === '(' ? metadata.actions.find(candidate => candidate.key === frames[0].name) : undefined;

      // Inside a TARIFF parameter: its parts, their fields, and the bands of a range.
      const tariffParam = call?.params.find(param => param.name === frames[1]?.param && String(param.paramType) === 'TARIFF');
      if (tariffParam && frames[1].bracket === '[' && top) {
        const tariff = tariffCompletion(frames.slice(1), word.from, context.state.sliceDoc(word.from, word.to), tr, loadRefOptions);
        if (tariff !== undefined) return tariff;
      }

      // Inside an action's parentheses the useful completions are its parameter names.
      if (call && top === frames[0]) {
        const action = call;
        const written = topLevelText(top.content);
        // After `name:` an enum parameter offers its values (`type: FUEL_SURCHARGE`).
        const named = (written.split(',').pop() ?? '').match(/([A-Za-z_][A-Za-z0-9_]*)\s*:\s*$/);
        if (named) {
          const param = action.params.find(candidate => candidate.name === named[1]);
          if (!param?.enumValues?.length) return null;
          const keyPrefix = paramEnumKeyPrefix(action.key, param.name);
          return { from: word.from, options: param.enumValues.map(value => option(value, enumValueLabel(tr, value, keyPrefix), 'constant')) };
        }
        const alreadyUsed = new Set(Array.from(written.matchAll(/([A-Za-z_][A-Za-z0-9_]*)\s*:/g)).map(match => match[1]));
        const params = action.params.filter(param => !alreadyUsed.has(param.name));
        return {
          from: word.from,
          options: params.map(param =>
            option(
              `${param.name}: `,
              param.required ? tr('AiDispatcher.paramRequired', '{{type}} (required)', { type: param.paramType }) : String(param.paramType),
              'property',
            ),
          ),
        };
      }
      if (top) return null;
      return { from: word.from, options: metadata.actions.map(action => option(action.key, tr(action.i18nKey, action.key), 'function')) };
    }

    if (section === 'CONDITION') {
      const trailing = before.trimEnd();

      // Inside a fact's parentheses: its argument names, or after `name:` that argument's values.
      // A fact without arguments offers nothing — the backend's diagnostic says it takes none.
      const openArgs = before.match(/([A-Za-z_][A-Za-z0-9_]*\.[A-Za-z0-9_.]+)\s*\(([^()]*)$/);
      if (openArgs) {
        const parameters = factParametersOf(metadata.facts.find(candidate => candidate.key === openArgs[1]));
        const current = openArgs[2].split(',').pop() ?? '';
        const named = current.match(/([A-Za-z_][A-Za-z0-9_]*)\s*:\s*$/);
        if (named) {
          const parameter = parameters.find(candidate => candidate.name === named[1]);
          if (!parameter) return null;
          return { from: word.from, options: parameter.parameterValues.map(value => option(value, argValueLabel(tr, parameter, value), 'constant')) };
        }
        const alreadyUsed = new Set(Array.from(openArgs[2].matchAll(/([A-Za-z_][A-Za-z0-9_]*)\s*:/g)).map(match => match[1]));
        const remaining = parameters.filter(parameter => !alreadyUsed.has(parameter.name));
        if (remaining.length === 0) return null;
        return { from: word.from, options: remaining.map(parameter => option(`${parameter.name}: `, tr(parameter.i18nKey, parameter.name), 'property')) };
      }

      // A fact's own values: its enum values, or the list its entityRefType names (countries).
      const valuesOfFact = (factKey: string, includeFetched: boolean): CompletionResult | null | Promise<CompletionResult | null> => {
        const fact = metadata.facts.find(candidate => candidate.key === factKey);
        if (fact?.enumValues?.length) {
          return { from: word.from, options: fact.enumValues.map(value => option(value, enumValueLabel(tr, value), 'constant')) };
        }
        if (fact && String(fact.factType) === String(FactTypeEnum.DURATION)) {
          return {
            from: word.from,
            options: DURATION_EXAMPLES.map(([amount, unit]) =>
              snippetCompletion(`\${${amount}} \${${unit}}`, { label: `${amount} ${unit}`, detail: tr('AiDispatcher.durationDetail', 'a duration'), type: 'constant' }),
            ),
          };
        }
        // A date and time can be compared with a time relative to when the rule runs. The snippet
        // lets the author tab through the amount and the unit.
        if (fact && String(fact.factType) === String(FactTypeEnum.DATETIME)) {
          return {
            from: word.from,
            options: [
              option('now', tr('AiDispatcher.nowDetail', 'when the rule runs'), 'keyword'),
              snippetCompletion('now + ${2} ${hours}', {
                label: 'now + 2 hours',
                detail: tr('AiDispatcher.nowOffsetDetail', 'relative to when the rule runs'),
                type: 'keyword',
              }),
            ],
          };
        }
        const loading = includeFetched && fact?.entityRefType ? loadRefOptions?.(fact.entityRefType) : undefined;
        if (!loading) return null;
        // A failed load offers nothing; the builder's picker is where the error shows.
        const typed = context.state.sliceDoc(word.from, word.to);
        return loading.then(
          items => ({ from: word.from, filter: false, options: matchRefItems(items, typed).map(item => option(item.value, item.label, 'constant')) }),
          () => null,
        );
      };

      // Directly after an operator. The group operators come before plain `in` so they win.
      const afterOperator = trailing.match(
        new RegExp(String.raw`${FACT_REF}\s*(==|!=|>=|<=|>|<|\bnot in group\b|\bin group\b|\bin\b|\bnot in\b|\bcontains\b|\bstarts with\b|\bbetween\b)$`),
      );
      if (afterOperator) {
        // An operator with its own value list (`in group` -> country groups) offers that list.
        const operator = metadata.operators.find(candidate => candidate.symbol === afterOperator[2]);
        const ownList = metadataRefOptions(metadata, operator?.valueEntityRefType);
        if (ownList) {
          return { from: word.from, options: ownList.map(item => option(item.value, item.label, 'constant')) };
        }
        // A fetched list holds single values, so it waits for the `[` after `in` / `not in`.
        const listOperator = afterOperator[2] === 'in' || afterOperator[2] === 'not in';
        return valuesOfFact(afterOperator[1], !listOperator);
      }

      // The second bound of `between <value> and`. The first bound is matched as one value, so the
      // `and` that joins two conditions after a complete `between` is not mistaken for this one.
      const betweenAnd = trailing.match(
        new RegExp(String.raw`${FACT_REF}\s+between\s+(?:now(?:\s*[+-]\s*\d+(?:\.\d+)?\s+[A-Za-z]+)?|"[^"]*"|-?\d+(?:\.\d+)?(?:\s*[A-Za-z%]+)?)\s+and$`),
      );
      if (betweenAnd) return valuesOfFact(betweenAnd[1], true);

      // Inside the brackets of `in [...]` / `not in [...]`: the same values, one item at a time.
      const inList = trailing.match(new RegExp(String.raw`${FACT_REF}\s*(\bnot in\b|\bin\b)\s*\[[^[\]]*$`));
      if (inList) return valuesOfFact(inList[1], true);

      // Directly after a fact: offer the operators its type allows, written as they are typed.
      const afterFact = trailing.match(new RegExp(String.raw`${FACT_REF}$`));
      if (afterFact && word.from === word.to) {
        const fact = metadata.facts.find(candidate => candidate.key === afterFact[1]);
        if (fact) {
          const symbols = fact.operators.map(key => metadata.operators.find(operator => operator.key === key)).filter(operator => operator !== undefined);
          return { from: word.from, options: symbols.map(operator => option(operator.symbol, tr(operator.i18nKey, operator.symbol), 'keyword')) };
        }
      }

      const triggerType = before.match(/\bon\s+([A-Za-z0-9_.]+)/)?.[1];
      const facts = metadata.facts.filter(fact => !fact.availableForTriggers?.length || !triggerType || fact.availableForTriggers.includes(triggerType));
      return {
        from: word.from,
        options: [
          ...facts.map(fact => option(fact.key, `${tr(fact.i18nKey, fact.key)} · ${factGroupLabel(tr, fact.group)}`, 'variable')),
          // A fact with arguments is offered a second time with all of them at their defaults, as
          // a snippet to tab through — the explicit form reads unambiguously later.
          ...facts.flatMap(fact => {
            const parameters = factParametersOf(fact);
            if (parameters.length === 0) return [];
            const written = parameters.map(parameter => `${parameter.name}: ${parameter.defaultValue}`).join(', ');
            // Numbered fields: two arguments sharing a default must not become one linked field.
            const template = parameters.map((parameter, index) => `${parameter.name}: \${${index + 1}:${parameter.defaultValue}}`).join(', ');
            return [snippetCompletion(`${fact.key}(${template})`, { label: `${fact.key}(${written})`, detail: tr(fact.i18nKey, fact.key), type: 'variable' })];
          }),
          ...['and', 'or', 'not'].map(keyword => option(keyword, '', 'keyword')),
        ],
      };
    }

    return null;
  };
};

// ---------------------------------------------------------------------------
// Brackets and records
// ---------------------------------------------------------------------------

/** One bracket still open before the cursor. */
export interface OpenBracket {
  bracket: '(' | '[';
  /** For `(`: the word before it — an action or a record (`unit_price`, `band`). */
  name?: string;
  /** The parameter or field this bracket is the value of — `bands` for `bands: [`. */
  param?: string;
  /** What has been written inside it so far. */
  content: string;
}

/**
 * The brackets open at the end of `text`, outermost first. Strings and comments are skipped, so a
 * `(` in `"Call (before) arrival"` does not count. Replaces a single `name(...)$` regex, which could
 * not see past a record already closed inside the call — `price_tariff: [unit_price(…)], `.
 */
export const openBrackets = (text: string): OpenBracket[] => {
  const stack: { bracket: '(' | '['; name?: string; param?: string; start: number }[] = [];
  let i = 0;
  while (i < text.length) {
    const char = text[i];
    if (char === '"') {
      i++;
      while (i < text.length && text[i] !== '"') i += text[i] === '\\' ? 2 : 1;
    } else if (char === '#' || (char === '/' && text[i + 1] === '/')) {
      while (i < text.length && text[i] !== '\n') i++;
    } else if (char === '/' && text[i + 1] === '*') {
      const close = text.indexOf('*/', i + 2);
      i = close < 0 ? text.length : close + 1;
    } else if (char === '(' || char === '[') {
      const preceding = text.slice(0, i);
      const name = char === '(' ? preceding.match(/([A-Za-z_][A-Za-z0-9_]*)\s*$/)?.[1] : undefined;
      const param = (
        char === '(' ? preceding.match(/([A-Za-z_][A-Za-z0-9_]*)\s*:\s*[A-Za-z_][A-Za-z0-9_]*\s*$/) : preceding.match(/([A-Za-z_][A-Za-z0-9_]*)\s*:\s*$/)
      )?.[1];
      stack.push({ bracket: char, name, param, start: i });
    } else if (char === ')' || char === ']') {
      stack.pop();
    }
    i++;
  }
  return stack.map(({ start, ...frame }) => ({ ...frame, content: text.slice(start + 1) }));
};

/** The text with every nested bracket and string taken out, so only this level's `name:`s remain. */
const topLevelText = (content: string): string => {
  let depth = 0;
  let inString = false;
  let result = '';
  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    if (inString) {
      if (char === '\\') i++;
      else if (char === '"') inString = false;
      continue;
    }
    if (char === '"') inString = true;
    else if (char === '(' || char === '[') depth++;
    else if (char === ')' || char === ']') depth--;
    else if (depth === 0) result += char;
  }
  return result;
};

/**
 * The fields of the records a `price_tariff` is written in. The metadata types the parameter as
 * TARIFF and says nothing of its inside, so the record grammar — fixed by the language, like the
 * keywords — is declared here.
 */
const TARIFF_RECORDS: Record<string, string[]> = {
  unit_price: ['by', 'price'],
  range_price: ['by', 'bands'],
  band: ['from', 'to', 'price'],
};

const KM = 'km';
const HANDLING_UNIT = 'HANDLING_UNIT';

/**
 * Completion inside a tariff. `frames` starts at the tariff's own `[`. Returns undefined when the
 * cursor is somewhere this does not know about, so the caller falls through.
 */
const tariffCompletion = (
  frames: OpenBracket[],
  from: number,
  typed: string,
  tr: Translate,
  loadRefOptions?: RefOptionsLoader,
): CompletionResult | null | Promise<CompletionResult | null> | undefined => {
  const top = frames[frames.length - 1];
  const written = topLevelText(top.content);
  const atItemStart = /(^|,)\s*$/.test(written);

  // The list of parts.
  if (frames.length === 1) {
    if (!atItemStart) return null;
    return {
      from,
      options: [
        snippetCompletion('unit_price(by: ${1}, price: ${2})', {
          label: 'unit_price(by: , price: )',
          detail: tr('AiDispatcher.tariff.unitPriceDetail', 'a rate per km or unit'),
          type: 'function',
        }),
        snippetCompletion('range_price(by: ${1}, bands: [band(from: ${2}, to: ${3}, price: ${4})])', {
          label: 'range_price(by: , bands: [band(from: , to: , price: )])',
          detail: tr('AiDispatcher.tariff.rangePriceDetail', 'a flat price per range'),
          type: 'function',
        }),
      ],
    };
  }

  // The bands of a range.
  if (top.bracket === '[' && top.param === 'bands') {
    if (!atItemStart) return null;
    return { from, options: [snippetCompletion('band(from: ${1}, to: ${2}, price: ${3})', { label: 'band(from: , to: , price: )', type: 'function' })] };
  }

  // What a part measures: `km`, or handling-unit codes — inside `by: [` only the codes, as km stands alone.
  const named = (written.split(',').pop() ?? '').match(/([A-Za-z_][A-Za-z0-9_]*)\s*:\s*$/);
  const inBasisList = top.bracket === '[' && top.param === 'by';
  if ((top.bracket === '(' && named?.[1] === 'by') || inBasisList) {
    const km = !inBasisList && KM.startsWith(typed.toLowerCase()) ? [option(KM, tr('AiDispatcher.tariff.km', 'km (distance)'), 'constant')] : [];
    const loading = loadRefOptions?.(HANDLING_UNIT);
    if (!loading) return km.length ? { from, filter: false, options: km } : null;
    return loading.then(
      items => ({ from, filter: false, options: [...km, ...matchRefItems(items, typed).map(item => option(item.value, item.label, 'constant'))] }),
      () => (km.length ? { from, filter: false, options: km } : null),
    );
  }

  // A record's own fields, those not written yet.
  const fields = top.bracket === '(' ? TARIFF_RECORDS[top.name ?? ''] : undefined;
  if (!fields) return undefined;
  if (named) return null;
  const alreadyUsed = new Set(Array.from(written.matchAll(/([A-Za-z_][A-Za-z0-9_]*)\s*:/g)).map(match => match[1]));
  return { from, options: fields.filter(field => !alreadyUsed.has(field)).map(field => option(`${field}: `, '', 'property')) };
};

// ---------------------------------------------------------------------------
// Diagnostics
// ---------------------------------------------------------------------------

/**
 * Maps backend diagnostics onto editor ranges. Line and column are 1-based, as an editor counts.
 * A diagnostic with `line: 0` came from a builder edit and has nowhere to point, so it is dropped
 * here and shown in the panel instead.
 *
 * The text is rendered by the caller: messages are Locize keys with positional parameters, and
 * translation needs a React hook this module has no business holding.
 */
export const toEditorDiagnostics = (diagnostics: DiagnosticOutDTO[], doc: Text, renderMessage: (diagnostic: DiagnosticOutDTO) => string): Diagnostic[] =>
  diagnostics
    .filter(diagnostic => diagnostic.line > 0 && diagnostic.line <= doc.lines)
    .map(diagnostic => {
      const line = doc.line(diagnostic.line);
      const from = Math.min(line.from + Math.max(diagnostic.column - 1, 0), line.to);
      const to = Math.min(from + Math.max(diagnostic.length, 1), line.to);
      return {
        from,
        to: to > from ? to : Math.min(from + 1, doc.length),
        severity: diagnostic.diagnosticSeverity === DiagnosticSeverityEnum.ERROR ? ('error' as const) : ('warning' as const),
        message: renderMessage(diagnostic),
      };
    });
